/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description Add Note
 */
import { useState } from 'react';
import _ from 'lodash';
import { Button, Modal, Input, Select, Form, ButtonProps } from 'antd';
import { Plus } from '@icon-park/react';

import useAddActivityLog from 'hooks/claim/mutate/useAddActivityLog';
import { SYSTEM_DEFINED_ID_PREFIX } from 'utils/admin/entitySettings';
import { AntClass } from '@veritable/common/constants/antd';
import { WORKLIST_ENTITY_TYPES } from 'app-constants/claims/workLists';

export interface AddNoteProps extends ButtonProps {
	activityTypes: ClaimRequest.IActivityType[];
	claimId: string;
}

interface AddNoteState {
	isModalOpen: boolean;
	note: string;
	selectedAction?: string;
}

const initialState: AddNoteState = {
	isModalOpen: false,
	note: '',
	selectedAction: undefined,
};

/**
 * AddNote Component
 * Modal for adding notes with optional action selection
 */
const AddNote: React.FC<AddNoteProps> = ({ activityTypes, claimId, ...restProps }) => {
	const [state, setState] = useState<AddNoteState>(initialState);
	const { mutateAsync, isPending } = useAddActivityLog();

	// filter out system activity types
	const actionableActivityTypes = _.filter(
		activityTypes,
		(activityType) => !_.startsWith(activityType?.id, SYSTEM_DEFINED_ID_PREFIX) && activityType?.isDeleted === false,
	);

	// prepare options for the select dropdown, it should only have user activity types
	const options = _.map(actionableActivityTypes, (activityType) => ({
		label: activityType?.name,
		value: activityType?.id,
	}));

	const handleCancel = () => {
		setState(initialState);
	};

	const handleAdd = async () => {
		const selectedAction = state?.selectedAction || _.find(options, (option) => option.label === 'Other')?.value;
		await mutateAsync({
			entityType: WORKLIST_ENTITY_TYPES.claim,
			entityId: claimId,
			model: {
				description: state?.note,
				activityTypeId: selectedAction,
			},
		});
		handleCancel();
	};

	const isAddDisabled = _.isEmpty(state?.selectedAction);

	return (
		<>
			<Button
				type='primary'
				className={AntClass.secondaryButton}
				onClick={() => setState((prev) => ({ ...prev, isModalOpen: true }))}
				icon={<Plus />}
				iconPosition='start'
				{...restProps}
			>
				{' Add Activity'}
			</Button>

			<Modal
				title='Add Activity'
				open={state.isModalOpen}
				onCancel={handleCancel}
				okButtonProps={{ disabled: isAddDisabled, loading: isPending }}
				okText={'Add'}
				onOk={handleAdd}
				cancelButtonProps={{ disabled: isPending }}
				destroyOnHidden
				width={600}
			>
				<div className='flex flex-col gap-4'>
					<Form layout='vertical'>
						<div>
							<Form.Item label='Activity' name='action' rules={[{ required: true, message: 'Select an activity' }]}>
								<Select
									placeholder='Search and select an activity'
									value={state.selectedAction}
									onChange={(value) => setState((prev) => ({ ...prev, selectedAction: value }))}
									options={options}
									className='w-full'
									allowClear
									showSearch
									optionFilterProp='label'
								/>
							</Form.Item>
						</div>
						<div>
							<Form.Item label='Note' name='note'>
								<Input.TextArea
									placeholder='Enter your note here'
									value={state.note}
									onChange={(e) => setState((prev) => ({ ...prev, note: e.target.value }))}
									autoSize={{ minRows: 1, maxRows: 4 }}
								/>
							</Form.Item>
						</div>
					</Form>
				</div>
			</Modal>
		</>
	);
};

export default AddNote;
